import { makeObservable, observable, action, computed } from 'mobx';
import ColorScales from '../UtilityClasses/ColorScales';
import { isNumeric, isCategorical, isBinary, isOrdinal, isCategoricalLike } from '../UtilityClasses';

/**
 * a derived variable is derived of one or multiple other variables
 */
class Variable {
	/**
	 * constructs a variable
	 * @param {string} id
	 * @param {string} name
	 * @param {string} datatype - ORDINAL, STRING, NUMBER, BINARY
	 * @param {string} description
	 * @param {string[]} originalIds - ids of variables that were used to derive the variable
	 * @param {string[]} range
	 * @param {(number[]|string[]|boolean[])} domain
	 * @param {Object} mapper - mapper mapping sampleIds to values
	 * @param {string} profile - profile (group of variables) that this variable belongs to
	 * @param {string} type  - gene, clinical, computed, event, derived
	 */
	constructor(id, name, datatype, description, originalIds, range, domain, mapper, profile, type) {
		this.id = id;
		this.datatype = datatype;
		this.description = description;
		this.originalIds = originalIds;
		this.mapper = mapper;
		this.type = type;
		this.profile = profile;
		this.referenced = 0; // number of variables that reference this variable

		const currDomain = this.createDomain(domain);
		const currRange = ColorScales.createRange(currDomain, range, this.datatype);

		this.domain = currDomain;
		this.range = currRange;
		this.name = name;

		makeObservable(this, {
			domain: observable,
			range: observable,
			name: observable,
			colorScale: computed,
			changeRange: action,
			changeDomain: action,
			changeName: action,
		});
	}

	/**
	 * changes range
	 */
	changeRange = (newRange) => {
		this.range = newRange;
	};

	/**
	 * changes domain
	 */
	changeDomain = (newDomain) => {
		this.domain = newDomain;
	};

	/**
	 * changes name
	 */
	changeName = (newName) => {
		this.name = newName;
	};

	/**
	 * gets color scale
	 */
	get colorScale() {
		let scale;
		if (isCategorical(this.datatype) || isBinary(this.datatype)) {
			scale = ColorScales.getCategoricalScale(this.range, this.domain);
		} else if (isOrdinal(this.datatype)) {
			if (this.derived && this.modification.type === 'continuousTransform') {
				scale = ColorScales.getCategoricalScale(
					ColorScales.getBinnedRange(this.range, this.modification.binning.bins),
					this.domain
				);
			} else {
				scale = ColorScales.getOrdinalScale(this.range, this.domain);
			}
		} else if (isNumeric(this.datatype)) {
			scale = ColorScales.getContinousColorScale(this.range, this.domain);
		}
		return scale;
	}

	/**
	 * creates domain (use provided domain if given, otherwise use default domain)
	 * @param {(number[]|string[]|boolean[])} domain
	 * @returns {(number[]|string[]|boolean[])} default domain or provided domain
	 */
	createDomain(domain) {
		if (
			(isNumeric(this.datatype) && domain.length !== 2) ||
			(isBinary(this.datatype) && domain.length === 0)
		) {
			return this.getDefaultDomain();
		}
		if (isCategoricalLike(this.datatype)) {
			return Array.from(new Set(domain.concat(...this.getDefaultDomain())));
		}
		return domain;
	}

	/**
	 * creates default domain
	 * @returns {(number[]|string[]|boolean[])} default domain
	 */
	getDefaultDomain() {
		if (isNumeric(this.datatype)) {
			return [
				Math.min(...Object.values(this.mapper).filter((d) => d !== undefined)),
				Math.max(...Object.values(this.mapper).filter((d) => d !== undefined)),
			];
		}
		if (isBinary(this.datatype)) {
			return [true, false];
		}
		return [...new Set(Object.values(this.mapper))].filter((d) => d !== undefined).sort();
	}
}

export default Variable;
